import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Copy, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicPortfolioPage() {
  const { portfolioId } = useParams();
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPortfolio();
  }, [portfolioId]);

  const fetchPortfolio = async () => {
    try {
      const response = await axios.get(`${API}/portfolios/${portfolioId}`);
      setPortfolio(response.data);
    } catch (error) {
      toast.error('Portfolio not found');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: '#FFFFFF' }}>
      <div className="text-xl" style={{ color: '#0B0B0B' }}>Loading...</div>
    </div>;
  }

  if (!portfolio) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: '#FFFFFF' }}>
      <div className="text-xl" style={{ color: '#0B0B0B' }}>Portfolio not found</div>
    </div>;
  }

  const activationProgress = portfolio.activation
    ? (portfolio.activation.contributed_amount / portfolio.activation_threshold_value) * 100
    : 0;

  return (
    <div className="min-h-screen" style={{ background: '#FFFFFF' }}>
      <header className="border-b" style={{ borderColor: 'rgba(75, 157, 255, 0.2)', background: 'rgba(43, 10, 89, 0.1)' }}>
        <div className="container mx-auto px-4 py-6">
          <h1 className="text-3xl font-bold" style={{ color: '#4B9DFF', fontFamily: 'Space Grotesk' }}>{portfolio.name}</h1>
          <p className="text-lg mt-2" style={{ color: '#0B0B0B' }}>{portfolio.description}</p>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card className="glass-card border-primary/20">
              <CardHeader>
                <CardTitle style={{ color: '#4B9DFF' }}>Portfolio Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm" style={{ color: '#0B0B0B', opacity: 0.7 }}>Status</p>
                    <span className="inline-block text-sm font-semibold px-3 py-1 rounded mt-1" style={{ background: portfolio.status === 'LIVE' ? 'rgba(71, 255, 176, 0.2)' : 'rgba(75, 157, 255, 0.2)', color: portfolio.status === 'LIVE' ? '#47FFB0' : '#4B9DFF' }}>
                      {portfolio.status}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: '#0B0B0B', opacity: 0.7 }}>Kickback</p>
                    <p className="text-2xl font-bold" style={{ color: '#47FFB0' }}>{portfolio.kickback_percent}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card border-primary/20">
              <CardHeader>
                <CardTitle style={{ color: '#4B9DFF' }}>Activation Progress</CardTitle>
                <CardDescription style={{ color: '#0B0B0B' }}>Goal: ${portfolio.activation_threshold_value.toLocaleString()}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span style={{ color: '#0B0B0B' }}>Contributed</span>
                    <span className="text-xl font-bold" style={{ color: '#47FFB0' }}>${portfolio.activation?.contributed_amount?.toLocaleString() || 0}</span>
                  </div>
                  <div className="progress-bar">
                    <div className="progress-bar-fill" style={{ width: `${Math.min(activationProgress, 100)}%` }} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            {portfolio.nft_contract_address && (
              <Card className="glass-card border-primary/20">
                <CardHeader>
                  <CardTitle style={{ color: '#4B9DFF' }}>Contract Address</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="p-3 rounded font-mono text-xs break-all" style={{ background: 'rgba(43, 10, 89, 0.3)', color: '#47FFB0' }}>
                      {portfolio.nft_contract_address}
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => copyToClipboard(portfolio.nft_contract_address)} variant="outline" size="sm" className="flex-1 border-primary/30">
                        <Copy className="w-4 h-4 mr-2" />Copy
                      </Button>
                      <Button onClick={() => window.open(`https://amoy.polygonscan.com/address/${portfolio.nft_contract_address}`, '_blank')} variant="outline" size="sm" className="flex-1 border-primary/30">
                        <ExternalLink className="w-4 h-4 mr-2" />View
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
